import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  BarChart,
  Button,
  Card,
  DonutChart,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  StackedBarChart,
} from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type {
  Failure,
  FailureAcknowledgement,
  FailureDetail,
  FailurePage,
  FailureSummary,
  FailureWindow,
  Operator,
  Reason,
} from "../admin-client";
import {
  useAcknowledgeFailures,
  useAcknowledgeMatchingFailures,
  useApplications,
  useFailure,
  useFailureSummary,
  useFailures,
  useOpenFailures,
} from "../admin-hooks";
import { RequireOperator, can, errorMessage } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { NotPermitted } from "./packages";
// `when`, not `whenExact`: sub-second precision is for a TRAIL, where the
// order of two lines written in one transaction is the fact being read. A
// fault has one first occurrence and one last, seconds apart at the closest,
// and "4:11:09 AM.123" is a millisecond nailed to the end of a locale string.
import { CopyText, ShortID, when } from "./common";
import {
  ACKNOWLEDGED_OPTIONS,
  CLEARING_HIDES,
  FAILURES_CLEAR,
  FAILURES_READ,
  FAILURE_KINDS,
  FAILURE_SERVICES,
  FAILURE_SEVERITIES,
  NO_FILTER,
  acknowledgementNote,
  applicationOptions,
  clearRefusal,
  clearScope,
  contextIsEmpty,
  contextText,
  describeAcknowledgement,
  describeClearScope,
  describeRefusal,
  describeSelection,
  describeTotals,
  describeWindow,
  disagreeingDays,
  effectiveSelection,
  emptyBody,
  emptyTitle,
  failureChartState,
  failureDetailState,
  failureListState,
  failureQueryFrom,
  filterChips,
  groupPoints,
  isNarrowed,
  navBadge,
  occurrenceCount,
  occurrenceLabel,
  overCeiling,
  selectAllState,
  selectableIDs,
  severityDaySeries,
  severityTone,
  toggleSelected,
  windowMismatchNote,
  withoutFilter,
} from "./failure-log";
import type {
  ClearRefusal,
  ClearScope,
  FailureFilterField,
  FailureListState,
  FailuresSearch,
} from "./failure-log";

/**
 * `/failures` — what the SOFTWARE is doing wrong.
 *
 * The sibling of `/audit`, and deliberately a separate screen from it: the
 * audit trail is what OPERATORS did, this is what the platform did, and the two
 * answer to different people under different permissions. An operator reading
 * this one is asking "is anything broken", and every design decision below
 * follows from the two ways that question gets answered wrongly.
 *
 * ONE ROW IS ONE FAULT, NOT ONE FAILURE. `ops.system_failures` is keyed on a
 * fingerprint: a repeat moves `last_occurred_at` and increments
 * `occurrence_count` rather than adding a row. So the occurrence count is a
 * column of its own, drawn large, and it is the one number on the page that
 * must not be skimmed past — twelve rows can be four million failures.
 *
 * AN EMPTY LIST IS GOOD NEWS ONLY WHEN NOTHING IS FILTERING IT. "Nothing is
 * failing" is a real and excellent answer and it is rendered as an answer; but
 * it is a claim about the PLATFORM, so it is only made when the URL narrows
 * nothing. Under a filter the same empty body says so instead, and the chips
 * above it say what to remove.
 *
 * The screen is ABSENT for an operator without `failures:read` — the nav link
 * too — matching every other guarded surface in this console. The Admin API
 * re-checks the grant and audits the refusal; this only spares the click.
 */
export function Failures({ search }: { search: FailuresSearch }) {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, FAILURES_READ) ? (
          <FailuresScreen search={search} operator={session.operator} />
        ) : (
          <NotPermitted title="Failures" permission={FAILURES_READ} />
        )
      }
    </RequireOperator>
  );
}

/**
 * The nav entry, with the open-fault count on it.
 *
 * In the nav rather than only on the screen because the console has to surface
 * an incident without being opened: an operator who came here to reactivate an
 * account should be able to see, without navigating, that fourteen things are
 * broken. It is the same read the screen makes when it is unfiltered — one
 * cache entry, two readers — and `enabled` is the operator's own grant, so an
 * operator who may not read the log does not issue a request that would be
 * refused and audited on every screen they open.
 *
 * The badge has four renders and `navBadge` owns all four; the important one is
 * that a FAILED read shows `?` rather than nothing. A badge that disappears
 * when the log cannot be read would report a healthy platform from a read that
 * never happened. Its counterpart is the LOADING render, which is nothing at
 * all: a nav that flickers a spinner on every navigation is a nav an operator
 * stops reading.
 */
export function FailuresNavLink({ mayRead }: { mayRead: boolean }) {
  const page = useOpenFailures(mayRead);
  if (!mayRead) {
    return null;
  }
  const badge = navBadge(
    Boolean(page.error),
    Boolean(page.data),
    page.data?.failures.length ?? 0,
    page.data?.truncated ?? false,
  );
  return (
    <Link to="/failures" search={NO_FILTER}>
      Failures
      {badge ? (
        <>
          {" "}
          <span title={badge.title}>
            <Badge tone={badge.tone}>
              <span aria-hidden="true">{badge.text}</span>
              <span className="esm-visually-hidden">{badge.title}</span>
            </Badge>
          </span>
        </>
      ) : null}
    </Link>
  );
}

function FailuresScreen({
  search,
  operator,
}: {
  search: FailuresSearch;
  operator: Operator | undefined;
}) {
  const navigate = useNavigate();

  // The form's own state, which is what the operator is TYPING. The URL is what
  // has been asked for, and the two are deliberately different things until
  // Apply — same shape as the audit filter, and for the same reason: a select
  // that navigated on change would put a half-built filter in the history.
  const [severity, setSeverity] = useState(search.severity ?? "");
  const [service, setService] = useState(search.service ?? "");
  const [kind, setKind] = useState(search.kind ?? "");
  const [application, setApplication] = useState(search.application ?? "");
  // Defaulted to the SERVER's default rather than to "", because this filter is
  // three-valued and has no "any": a select whose value matches no option shows
  // its first option while holding something else, so the control and the list
  // would agree only by coincidence.
  const [acknowledged, setAcknowledged] = useState(search.acknowledged ?? "false");
  const [since, setSince] = useState(search.since ?? "");
  const [until, setUntil] = useState(search.until ?? "");

  // The URL is the authority. A chip removed, the back button, or a link
  // followed from a row all move it without the form knowing, and a form left
  // showing the previous filter is a control that lies about what is on screen.
  useEffect(() => {
    setSeverity(search.severity ?? "");
    setService(search.service ?? "");
    setKind(search.kind ?? "");
    setApplication(search.application ?? "");
    setAcknowledged(search.acknowledged ?? "false");
    setSince(search.since ?? "");
    setUntil(search.until ?? "");
  }, [
    search.severity,
    search.service,
    search.kind,
    search.application,
    search.acknowledged,
    search.since,
    search.until,
  ]);

  // Which rows are open. A Set rather than one id: two faults open at once is a
  // comparison, and an accordion that closed the other would answer a question
  // the operator did not ask.
  const [openRows, setOpenRows] = useState<ReadonlySet<string>>(() => new Set<string>());

  // Which rows are TICKED. Held as ids rather than as rows, and intersected
  // with the page at render time by `effectiveSelection`, so a tick cannot
  // outlive the row it was put on: the list refetches after every
  // acknowledgement and on every filter change, and clearing a fault nobody is
  // looking at is the one act this screen must be structurally unable to make.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set<string>());

  // What the last act said, and what it refused. Two pieces of state and not
  // one: a refusal is the operator's next task ("narrow this filter") and an
  // outcome is a receipt, and a screen showing one where the other belongs
  // sends somebody looking for a problem that is not there.
  const [outcome, setOutcome] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<ClearRefusal | null>(null);
  // The server's own bulk ceiling, learned from whatever it last answered —
  // every acknowledgement carries `max_matched`, and so does the refusal. Kept
  // rather than hard-coded, because a constant in this file is a copy of a
  // server number kept in step by remembering; null until something has
  // arrived, which is what `overCeiling` refuses to guess from.
  const [ceiling, setCeiling] = useState<number | null>(null);

  const query = failureQueryFrom(search);
  const page = useFailures(query);
  // The charts. The SAME query object, so the two documents are two views of
  // one set — but not the same window: the summary route always resolves one
  // and the list does not, which `windowMismatchNote` says out loud rather
  // than leaving the charts to look like a summary of the table.
  const summary = useFailureSummary(query);
  // Only for the application select's labels. Every `failures:read` holder also
  // holds `applications:read` — internal/admin asserts it — so this is not a
  // permission the screen has to reason about, and if it fails the select
  // degrades to the value that is already in the URL. See applicationOptions.
  const applications = useApplications();

  const clearSelection = useAcknowledgeFailures();
  const clearMatching = useAcknowledgeMatchingFailures();

  // Whether to draw the two clear controls AT ALL. Absent and not disabled —
  // the tick boxes too — matching every other guarded control in this console:
  // a disabled button is a promise that something could be done, and this
  // operator's roles say it cannot.
  const mayClear = can(operator, FAILURES_CLEAR);

  /**
   * One act, and the only place either clear's answer is read.
   *
   * THE BULK REFUSAL IS CAUGHT AND NOT RETHROWN. A filter matching more than
   * the server's ceiling comes back 422 with nothing written, and the operator's
   * next move is to narrow the filter — which is on the screen, behind the
   * modal. So the dialog closes and the refusal lands as a banner on the panel,
   * beside the controls that fix it. It is remembered too: the ceiling it
   * carries is what lets the NEXT confirmation warn before the request, inside
   * the dialog, where the operator can still cancel.
   *
   * Everything else rethrows on purpose. A 500, a 403 or a dropped connection
   * is not a filter that needs narrowing, and the reason dialog keeps those in
   * its own alert with the note the operator typed still in the box.
   */
  const runClear = async (act: Promise<FailureAcknowledgement>): Promise<void> => {
    let result: FailureAcknowledgement;
    try {
      result = await act;
    } catch (err) {
      const refused = isApiError(err)
        ? clearRefusal(err.status, err.code, err.message, err.problem)
        : null;
      if (!refused) {
        throw err;
      }
      setRefusal(refused);
      setOutcome(null);
      if (refused.ceiling !== null) {
        setCeiling(refused.ceiling);
      }
      return;
    }
    setRefusal(null);
    setOutcome(describeAcknowledgement(result));
    setCeiling(result.max_matched);
    // The ticks go with the act that spent them. Leaving them would offer a
    // second click that writes nothing and reports "already acknowledged".
    setSelected(new Set<string>());
  };

  const go = (next: FailuresSearch) => {
    // A new question, so the last answer's receipt and refusal stop applying.
    setOutcome(null);
    setRefusal(null);
    void navigate({ to: "/failures", search: { ...NO_FILTER, ...next } });
  };

  return (
    <>
      <PageHeader
        title="Failures"
        description="What the platform itself got wrong. One row is one FAULT — a repeat increments its count rather than adding a row — so read the occurrences column, not the number of rows."
      />

      <Card title="Filter">
        <form
          className="esm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            go({
              severity: severity || undefined,
              service: service || undefined,
              kind: kind || undefined,
              application: application || undefined,
              // The default is dropped rather than written: `?acknowledged=false`
              // and no parameter at all are the same question, and two links
              // that ask it should not look different. Anything else — including
              // a value a hand-edited URL put in the box — is carried, and the
              // server refuses what it cannot hold.
              acknowledged: acknowledged === "false" ? undefined : acknowledged || undefined,
              since: since || undefined,
              until: until || undefined,
              // Not in the form: they arrive from a row and are removed with
              // their chip. Carried across so applying the form does not widen
              // a filter the operator narrowed by following a link.
              user: search.user,
              device: search.device,
            });
          }}
        >
          <Field label="Acknowledgement" htmlFor="failures-acknowledged">
            <Select
              id="failures-acknowledged"
              value={acknowledged}
              onChange={(event) => setAcknowledged(event.target.value)}
            >
              {/* No "any" option: this filter is three-valued and "all" IS the
                  any. An empty value means "not asked", which the server
                  resolves to the open set. */}
              {ACKNOWLEDGED_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Severity" htmlFor="failures-severity">
            <Select
              id="failures-severity"
              value={severity}
              onChange={(event) => setSeverity(event.target.value)}
            >
              <option value="">Any severity</option>
              {FAILURE_SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Service" htmlFor="failures-service">
            <Select
              id="failures-service"
              value={service}
              onChange={(event) => setService(event.target.value)}
            >
              <option value="">Any service</option>
              {FAILURE_SERVICES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Kind" htmlFor="failures-kind">
            <Select id="failures-kind" value={kind} onChange={(event) => setKind(event.target.value)}>
              <option value="">Any kind</option>
              {FAILURE_KINDS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          {/* A select and not a box. The route answers 404 for an application
              it cannot resolve, and the picker is what makes that refusal
              unreachable from the screen — the same rule the model-usage
              replacement field follows. */}
          <Field label="Application" htmlFor="failures-application">
            <Select
              id="failures-application"
              value={application}
              onChange={(event) => setApplication(event.target.value)}
            >
              <option value="">Any application</option>
              {applicationOptions(
                search.application,
                applications.data?.applications ?? [],
              ).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          {/* Dates, not instants: the route filters on `occurred_at` by
              inclusive ISO DAY and refuses anything else. They bound when a
              fault was FIRST seen, which is not when it last fired — a filter
              that dropped an old fault still going would hide the incident. */}
          <Field
            label="First seen from"
            htmlFor="failures-since"
            hint="When the fault STARTED, not when it last fired."
          >
            <Input
              id="failures-since"
              type="date"
              value={since}
              onChange={(event) => setSince(event.target.value)}
            />
          </Field>
          <Field label="First seen to" htmlFor="failures-until">
            <Input
              id="failures-until"
              type="date"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
            />
          </Field>
          <Button type="submit" busy={page.isFetching}>
            Apply
          </Button>
        </form>
        <FilterChips search={search} onClear={(field) => go(withoutFilter(search, field))} onClearAll={() => go(NO_FILTER)} />
      </Card>

      <FailuresPanel
        search={search}
        page={page.data}
        error={page.error}
        summary={summary.data}
        openRows={openRows}
        onToggleRow={(id, open) =>
          setOpenRows((current) => {
            const next = new Set(current);
            if (open) {
              next.add(id);
            } else {
              next.delete(id);
            }
            return next;
          })
        }
        onFilter={go}
        mayClear={mayClear}
        selected={selected}
        onSelect={(id, on) => setSelected((current) => toggleSelected(current, id, on))}
        onSelectAll={(on) =>
          setSelected(on ? new Set(selectableIDs(page.data?.failures ?? [])) : new Set<string>())
        }
        ceiling={ceiling}
        outcome={outcome}
        refusal={refusal}
        onDismiss={() => {
          setOutcome(null);
          setRefusal(null);
        }}
        busy={clearSelection.isPending || clearMatching.isPending}
        onClearSelected={(ids, reason) =>
          runClear(clearSelection.mutateAsync({ failureIDs: ids, reason }))
        }
        onClearMatching={(reason) => runClear(clearMatching.mutateAsync({ query, reason }))}
      />

      {/* BELOW the table, not above it. The screen's headline answer is "what
          is open right now" and the nav badge points an operator straight at
          it; four cards of context between the heading and the rows would push
          the answer off the first screen. The charts are what the rows MEAN,
          and they read as the section under them. */}
      <FailureCharts
        summary={summary.data}
        error={summary.error}
        listWindow={page.data?.window}
      />
    </>
  );
}

/** One frozen empty Set, so `FailuresPanel`'s default does not build a new one
 *  on every render — a fresh identity per render is a prop that always differs,
 *  and the reason a default like this is written as a module constant. */
const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>();

/** Counts with separators. The one formatter every chart on this screen uses,
 *  so an axis tick and a table cell cannot disagree about what 12000 looks
 *  like. */
function count(value: number): string {
  return value.toLocaleString();
}

/**
 * The shape of what is breaking: four cards off `/admin/v1/failures/summary`.
 *
 * THE CHARTS AND THE TABLE ARE NOT LOOKING AT THE SAME DAYS unless the URL
 * carries dates. The summary route always resolves a window — thirty days by
 * default — because a daily series has to name the days it is counting, and the
 * list deliberately resolves none, so that an unacknowledged fault from months
 * ago stays on the console. Both report what they used, and the note at the top
 * of this section is that comparison. Without it the bars read as a summary of
 * the rows above them, which is the one thing they are not.
 *
 * Exported for the same reason `FailuresPanel` is: there is no browser on the
 * build box, so a section whose states are only reachable through a query is a
 * section that is only ever typechecked.
 */
export function FailureCharts({
  summary,
  error,
  listWindow,
}: {
  summary: FailureSummary | undefined;
  error: unknown;
  listWindow: FailureWindow | undefined;
}) {
  const state = failureChartState(Boolean(error), Boolean(summary), summary?.faults ?? 0);
  const mismatch =
    summary && listWindow ? windowMismatchNote(listWindow, summary.window) : null;

  if (state === "loading") {
    return (
      <Card title="Shape">
        <Spinner label="Counting the failure log" />
      </Card>
    );
  }
  if (state === "failed" || !summary) {
    return (
      <Card title="Shape">
        <Alert tone="danger" title="The charts could not be drawn">
          {errorMessage(error)} The table above is a separate read and is still an answer; what is
          missing is the shape, not the rows. In particular nothing here is the platform reporting
          that nothing is failing.
        </Alert>
      </Card>
    );
  }

  const days = severityDaySeries(summary.days);
  const disagreeing = disagreeingDays(summary.days);

  return (
    <>
      <Card title="Shape">
        {/* The window the CHARTS ran over, said before anything is drawn. */}
        <p className="esm-muted-text">{describeWindow(summary.window)}</p>
        {mismatch ? (
          <Alert tone="info" title="The charts and the table cover different days">
            {mismatch}
          </Alert>
        ) : null}
        {summary.window.clamped ? (
          <Alert tone="info" title="The charts' window was moved">
            The bars run {summary.window.since} to {summary.window.until}, which is not the span
            that was asked for.
          </Alert>
        ) : null}
        {state === "quiet" ? (
          <p>
            No fault STARTED in these days. That is a statement about this window and not about the
            platform: a fault that began before {summary.window.since ?? "the window"} and is still
            firing several times a second is in the table above and in none of the bars below,
            because a row is filed under the day it was first seen.
          </p>
        ) : (
          <p>{describeTotals(summary)}</p>
        )}
      </Card>

      {state === "quiet" ? null : (
        <>
          <Card title="New faults per day, by severity">
            <p className="esm-muted-text">
              NEW faults per day, not failures per day. A row is filed under the day it was FIRST
              seen, so a fault that started in March and has fired four million times since is one
              unit in March's bar and nothing at all in today's. Critical sits on the baseline,
              where it can be compared across days.
            </p>
            {disagreeing.length > 0 ? (
              <Alert tone="warning" title="A bar does not add up">
                {`The parts and the total disagree on ${disagreeing.join(", ")}. The bars draw the parts; treat the totals as unreliable and read the table below the chart.`}
              </Alert>
            ) : null}
            <StackedBarChart
              name="New faults per day by severity"
              labels={days.labels}
              series={days.series}
              format={count}
              pointHeader="Day"
            />
          </Card>

          <Card title="Faults by service">
            <p className="esm-muted-text">
              How many distinct things are broken in each deployment — FAULTS, not firings. A
              service with one fault that has fired ten thousand times has one bar here; the
              "fires most often" table below is where that row surfaces.
            </p>
            <BarChart
              name="Faults by service"
              points={groupPoints(summary.services, "faults")}
              format={count}
              pointHeader="Service"
              valueHeader="Faults"
            />
          </Card>

          <Card title="Faults by kind">
            <p className="esm-muted-text">
              The same faults divided the other way. A kind nothing produced is absent rather than
              a zero slice — a ring of eight labelled nothings is not a picture of anything.
            </p>
            <DonutChart
              name="Faults by kind"
              points={groupPoints(summary.kinds, "faults")}
              format={count}
              centerLabel={count(summary.faults)}
              centerCaption="faults"
              pointHeader="Kind"
              valueHeader="Faults"
            />
          </Card>

          <TopFaults summary={summary} />
        </>
      )}
    </>
  );
}

/**
 * The noisiest faults: ranked by LIFETIME occurrence count, which is a
 * different order from the list's.
 *
 * The list is newest-first, and the loudest thing on the platform is very
 * often not the newest — a fault that started weeks ago and has been firing
 * ever since is the one an operator most needs and the one a
 * most-recently-started page pushes off the bottom. The server does the
 * ranking; nothing here re-orders it, because a client that re-ranked the rows
 * a server already cut would be labelling the survivors of one ordering as the
 * top of another.
 */
function TopFaults({ summary }: { summary: FailureSummary }) {
  const rows = summary.top_fingerprints;
  return (
    <Card title="Fires most often">
      <p className="esm-muted-text">
        Ranked by how many times each fault has fired in its whole life, which includes firings from
        before this window — the table keeps no per-day occurrence history. A different order from
        the list above, deliberately: the loudest fault on the platform is rarely the newest.
      </p>
      {rows.length === 0 ? (
        <p className="esm-blank-value">Nothing matched, so there is nothing to rank.</p>
      ) : (
        // Scrolled rather than clipped: `.esm-card` sets `overflow: hidden`, so
        // a table wider than its card silently loses its right-hand columns,
        // with no scrollbar to say there are more. DataTable wraps itself;
        // this one is hand-rolled and has to say it.
        <div className="esm-table-scroll">
          <table className="esm-table">
            <thead>
              <tr>
                <th scope="col">Severity</th>
                <th scope="col">Where</th>
                <th scope="col">Kind</th>
                <th scope="col" className="esm-table--end">
                  Occurrences
                </th>
                <th scope="col">What broke</th>
                <th scope="col">Last fired</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Badge tone={severityTone(row.severity)}>{row.severity}</Badge>
                    <div className="esm-muted-text">{acknowledgementNote(row)}</div>
                  </td>
                  <td>
                    <code>{row.service}</code>
                    <div className="esm-muted-text">
                      <code className="esm-long-value" title={row.component}>
                        {row.component}
                      </code>
                    </div>
                  </td>
                  <td>
                    <code>{row.kind}</code>
                  </td>
                  <td className="esm-table--end">
                    <span className="esm-occurrences" title={occurrenceLabel(row.occurrence_count)}>
                      <span aria-hidden="true">{occurrenceCount(row.occurrence_count)}</span>
                      <span className="esm-visually-hidden">
                        {occurrenceLabel(row.occurrence_count)}
                      </span>
                    </span>
                  </td>
                  {/* Text, like every other rendering of a message in this file:
                      nothing out of the log chooses its own markup. */}
                  <td>
                    <span className="esm-failure__message">{row.message}</span>
                  </td>
                  <td>{when(row.last_occurred_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* A slice needs its count beside it. Reported even when false, so this
          can say "the noisiest N" rather than implying it has them all. */}
      {summary.top_fingerprints_truncated ? (
        <p className="esm-muted-text">
          These are the {rows.length} noisiest of the {count(summary.faults)} faults matching the
          filter, and there are more behind them.
        </p>
      ) : null}
    </Card>
  );
}

/**
 * What the URL is narrowing by, and the way back out of each one.
 *
 * `user` and `device` have no control in the form above — they are uuids
 * followed out of a row — so without a chip they would be a narrowing an
 * operator can neither see nor undo. Every other filter gets one too, because a
 * chip row that showed some filters and not others would be read as the whole
 * filter.
 */
export function FilterChips({
  search,
  onClear,
  onClearAll,
}: {
  search: FailuresSearch;
  onClear: (field: FailureFilterField) => void;
  onClearAll: () => void;
}) {
  const chips = filterChips(search);
  if (chips.length === 0) {
    return null;
  }
  return (
    <p className="esm-chips">
      <span className="esm-muted-text">Narrowed by</span>
      {chips.map((chip) => (
        <button
          key={chip.field}
          type="button"
          className="esm-chip"
          onClick={() => onClear(chip.field)}
        >
          {chip.label} <code>{chip.value}</code>
          <span aria-hidden="true"> ×</span>
          <span className="esm-visually-hidden"> — remove this filter</span>
        </button>
      ))}
      <Button type="button" variant="ghost" onClick={onClearAll}>
        Show everything
      </Button>
    </p>
  );
}

/**
 * Everything the screen DRAWS, and nothing it remembers.
 *
 * Split out on the rule `device-metadata-section.tsx` set: there is no browser
 * on the build box, so a branch reachable only by clicking is a branch that is
 * only ever typechecked. Every state below — the two failures, the four empty
 * answers, the cut page — is reachable from props alone.
 */
export function FailuresPanel({
  search,
  page,
  error,
  summary,
  openRows,
  onToggleRow,
  onFilter,
  mayClear = false,
  selected = EMPTY_SELECTION,
  onSelect = () => {},
  onSelectAll = () => {},
  ceiling = null,
  outcome = null,
  refusal = null,
  onDismiss = () => {},
  busy = false,
  onClearSelected = async () => {},
  onClearMatching = async () => {},
}: {
  search: FailuresSearch;
  page: FailurePage | undefined;
  error: unknown;
  /** The charts' document, here only for the FIGURE on the confirmation: it is
   *  the one thing that can count a cut page, and only when it ran over the
   *  same window. See `clearScope`. */
  summary?: FailureSummary | undefined;
  openRows: ReadonlySet<string>;
  onToggleRow: (id: string, open: boolean) => void;
  onFilter: (next: FailuresSearch) => void;
  mayClear?: boolean;
  selected?: ReadonlySet<string>;
  onSelect?: (id: string, on: boolean) => void;
  onSelectAll?: (on: boolean) => void;
  ceiling?: number | null;
  outcome?: string | null;
  refusal?: ClearRefusal | null;
  onDismiss?: () => void;
  busy?: boolean;
  onClearSelected?: (ids: string[], reason: Reason) => Promise<void>;
  onClearMatching?: (reason: Reason) => Promise<void>;
}) {
  const rows = page?.failures ?? [];
  const state = failureListState(Boolean(error), Boolean(page), rows.length, page?.truncated ?? false);
  // A 403 can still arrive: this console's permission list is the one from
  // sign-in, and a role changed underneath an open page is exactly where the
  // server's answer is the true one.
  const forbidden = isApiError(error) && error.status === 403;

  let body: ReactNode;
  if (state === "loading") {
    body = <Spinner label="Reading the failure log" />;
  } else if (state === "failed") {
    body = forbidden ? (
      <Alert tone="info" title="Not your permission">
        Reading the system failure log needs <code>{FAILURES_READ}</code>, which your roles do not
        grant.
      </Alert>
    ) : (
      <Alert tone="danger" title="Could not read the failure log">
        {errorMessage(error)} Nothing below is an answer — in particular, this is NOT the platform
        reporting that nothing is failing.
      </Alert>
    );
  } else if (state === "empty") {
    // The title and the body come off ONE pair of inputs — the RESPONSE's
    // acknowledgement filter and whether the URL narrows the set — because
    // "nothing is failing" is a claim about the platform and the other three
    // are not, and a body that did not branch would make the claim anyway.
    body = (
      <EmptyState title={emptyTitle(page?.acknowledged ?? "false", isNarrowed(search))}>
        {emptyBody(page?.acknowledged ?? "false", isNarrowed(search))}
      </EmptyState>
    );
  } else {
    body = (
      <FailureTable
        rows={rows}
        search={search}
        openRows={openRows}
        onToggleRow={onToggleRow}
        onFilter={onFilter}
        mayClear={mayClear}
        selected={selected}
        onSelect={onSelect}
        onSelectAll={onSelectAll}
      />
    );
  }

  // What the two controls are aimed at, worked out ONCE and handed to both the
  // confirmation and the panel — the acceptance criterion is that the exact
  // number appears in each, and two figures computed twice is how they come to
  // disagree.
  const scope = clearScope(page, summary);
  const ticked = effectiveSelection(selected, rows);

  return (
    <Card title={cardTitle(state, rows.length)}>
      {/* The window the ANSWER ran over, never the one that was asked for. The
          list has no default window on purpose — an unacknowledged fault that
          started months ago has to stay on the console — so "no date filter" is
          a real answer here and is said out loud. */}
      {page ? <p className="esm-muted-text">{describeWindow(page.window)}</p> : null}
      {page?.window.clamped ? (
        <Alert tone="info" title="The window was moved">
          The dates below are {page.window.since} to {page.window.until}, which is not the span
          that was asked for.
        </Alert>
      ) : null}
      {page?.application ? (
        <p className="esm-muted-text">
          Only faults recorded against <strong>{page.application.name}</strong> (
          <code>{page.application.slug}</code>).
        </p>
      ) : null}
      {/* THE FOURTH STATE. A cut page and a complete one are two different
          answers, and the server reports `truncated` even when false precisely
          so they cannot be confused. Narrowing is the only way to see the rest:
          the route has no cursor, and raising the page size would still be a
          page. */}
      {state === "truncated" ? (
        <Alert tone="warning" title="More faults match than this page shows">
          {/* The count is the rows DRAWN, which is the same number the heading
              above uses. The server's resolved `limit` is equal to it whenever
              this fires, and taking the two figures in one sentence off two
              sources is how they come to disagree. */}
          These are the {rows.length} most recently STARTED faults matching the filter, and there
          are more behind them. Narrow the filter — by service, by kind, or by a
          date range — to reach the rest. The count on each row is unaffected: every row still
          reports every time it has fired.
        </Alert>
      ) : null}
      {/* A refresh that failed over a page that is still on screen. The rows
          below are real and stay; what is no longer true is that they are
          current. */}
      {page && error ? (
        <Alert tone="danger" title="The list below could not be refreshed">
          {errorMessage(error)} What is shown is the last answer that arrived, and a fault that has
          started since is not on it.
        </Alert>
      ) : null}
      {/* The receipt from the last act and the refusal that stopped one, above
          the rows they are about. Both are dismissible: they describe something
          that has finished, and an alert that cannot be put away becomes part
          of the furniture. */}
      {outcome ? (
        <Alert tone="success" title="Cleared">
          {outcome} {CLEARING_HIDES}
        </Alert>
      ) : null}
      {refusal ? (
        <Alert tone="danger" title="Too many faults to clear in one act">
          {describeRefusal(refusal)}
        </Alert>
      ) : null}
      {outcome || refusal ? (
        <p>
          <Button variant="ghost" onClick={onDismiss}>
            Dismiss
          </Button>
        </p>
      ) : null}
      {body}
      {/* ABSENT for an operator without failures:clear — see ClearActions. */}
      {mayClear && (state === "listed" || state === "truncated") ? (
        <ClearActions
          scope={scope}
          ticked={ticked}
          ceiling={ceiling}
          busy={busy}
          onClearSelected={onClearSelected}
          onClearMatching={onClearMatching}
        />
      ) : null}
    </Card>
  );
}

/**
 * The two ways to take rows off the console, and everything either one says
 * before it does.
 *
 * ABSENT, not disabled, for an operator without the grant: the caller renders
 * nothing at all, tick boxes included. That is this console's standing rule for
 * a guarded control — a disabled button is a promise that the act exists for
 * this operator, and it does not.
 *
 * TWO CONTROLS, NOT ONE WITH A MODE. "The rows I ticked" and "everything this
 * filter matches" are different asks with different ceilings, and the server
 * refuses a request that carries both. A single button with a checkbox beside
 * it is one mis-set checkbox away from clearing a set nobody looked at.
 *
 * Both name the EXACT number of rows they will affect, and both say — in the
 * confirmation and again on the panel — that clearing hides rows rather than
 * deleting them. The figures come off `clearScope`/`describeSelection`, once,
 * so the sentence in the dialog and the sentence on the screen cannot drift.
 */
function ClearActions({
  scope,
  ticked,
  ceiling,
  busy,
  onClearSelected,
  onClearMatching,
}: {
  scope: ClearScope | null;
  ticked: string[];
  ceiling: number | null;
  busy: boolean;
  onClearSelected: (ids: string[], reason: Reason) => Promise<void>;
  onClearMatching: (reason: Reason) => Promise<void>;
}) {
  const selectionWords = describeSelection(ticked.length);
  const filterWords = scope ? describeClearScope(scope) : "";
  // Known to be over the server's ceiling BEFORE the request — which is only
  // knowable once something has answered, because the ceiling travels on the
  // response rather than being kept here as a constant. When it is, the
  // confirmation carries the refusal as a banner and the operator can cancel
  // out of it onto the filter instead of spending a round trip finding out.
  const refused = overCeiling(scope, ceiling);

  return (
    <div className="esm-clear">
      <p className="esm-muted-text">{CLEARING_HIDES}</p>
      <div className="esm-actions">
        <ReasonButton
          variant="secondary"
          busy={busy}
          disabled={ticked.length === 0}
          mutation={{
            title: "Clear the faults you have ticked",
            confirmLabel: `Clear ${ticked.length}`,
            description: (
              <>
                <p>{selectionWords}</p>
                <p>{CLEARING_HIDES}</p>
              </>
            ),
            run: (reason) => onClearSelected(ticked, reason),
          }}
        >
          {ticked.length === 0
            ? "Clear selected"
            : `Clear selected (${ticked.length})`}
        </ReasonButton>
        <ReasonButton
          variant="danger"
          busy={busy}
          mutation={{
            title: "Clear everything this filter matches",
            confirmLabel: "Clear them all",
            description: (
              <>
                {/* The banner the acceptance criteria ask for, inside the
                    dialog, which stays open until the operator decides. It can
                    only be drawn once a ceiling is known — see `overCeiling` —
                    and after one refusal it is, so a second act on a filter
                    that is still too wide is caught before the request. */}
                {refused ? (
                  <Alert tone="danger" title="This will be refused">
                    {ceiling === null
                      ? null
                      : `The server clears at most ${ceiling.toLocaleString()} faults in one act, and this filter matches more. Nothing will be written. Cancel, narrow the filter — by service, by kind, or by a date range — and clear it in pieces.`}
                  </Alert>
                ) : null}
                <p>{filterWords}</p>
                <p>
                  This is EVERY fault the filter matches, not only the rows drawn on the page. Read
                  the figure above before confirming.
                </p>
                <p>{CLEARING_HIDES}</p>
              </>
            ),
            run: (reason) => onClearMatching(reason),
          }}
        >
          Clear all matching filter
        </ReasonButton>
      </div>
      {/* The same two sentences on the panel, off the same two derivations. */}
      <p className="esm-muted-text">
        {ticked.length === 0
          ? "Nothing is ticked. Tick the rows you have dealt with, or clear everything the filter matches."
          : selectionWords}{" "}
        {filterWords}
      </p>
    </div>
  );
}

/** The heading counts FAULTS and says so. "50 failures" over a page of fifty
 *  fingerprints is the understatement this screen exists to avoid. */
function cardTitle(state: FailureListState, rows: number): string {
  switch (state) {
    case "loading":
      return "Faults";
    case "failed":
      return "Faults — unread";
    case "empty":
      return "Faults — none";
    case "truncated":
      return `Faults — the newest ${rows}, and there are more`;
    default:
      return rows === 1 ? "Faults — 1" : `Faults — ${rows}`;
  }
}

/**
 * The table.
 *
 * Hand-rolled rather than `DataTable`, because a row here is TWO rows: the
 * summary and, when it is open, the whole fault beneath it. One `<tbody>` per
 * fault keeps the pair together for a screen reader and for a stylesheet, which
 * a single flat `<tbody>` would not.
 */
function FailureTable({
  rows,
  search,
  openRows,
  onToggleRow,
  onFilter,
  mayClear,
  selected,
  onSelect,
  onSelectAll,
}: {
  rows: readonly Failure[];
  search: FailuresSearch;
  openRows: ReadonlySet<string>;
  onToggleRow: (id: string, open: boolean) => void;
  onFilter: (next: FailuresSearch) => void;
  mayClear: boolean;
  selected: ReadonlySet<string>;
  onSelect: (id: string, on: boolean) => void;
  onSelectAll: (on: boolean) => void;
}) {
  // The two counts the header's tick box is decided from. `selectable` is the
  // OPEN rows: a cleared row has no box, because ticking it would offer an act
  // that writes nothing and would make the confirmation's "exact number"
  // something the screen has to qualify.
  const selectable = selectableIDs(rows);
  const chosen = effectiveSelection(selected, rows);
  const all = selectAllState(chosen.length, selectable.length);

  return (
    // Scrolled rather than clipped: `.esm-card` sets `overflow: hidden`, so
    // a table wider than its card silently loses its right-hand columns,
    // with no scrollbar to say there are more. DataTable wraps itself;
    // this one is hand-rolled and has to say it.
    <div className="esm-table-scroll">
      <table className="esm-table esm-failures">
        <thead>
          <tr>
            {mayClear ? (
              <th scope="col" className="esm-failures__tick">
                <input
                  id="failures-select-all"
                  type="checkbox"
                  checked={all === "all"}
                  // A page with SOME of its open rows ticked is neither checked
                  // nor unchecked, and the indeterminate box is the only control
                  // that says so. `ref` rather than an attribute: it is a DOM
                  // property with no HTML spelling.
                  ref={(node) => {
                    if (node) {
                      node.indeterminate = all === "some";
                    }
                  }}
                  disabled={selectable.length === 0}
                  onChange={(event) => onSelectAll(event.target.checked)}
                />
                <label htmlFor="failures-select-all" className="esm-visually-hidden">
                  Tick every open fault on this page
                </label>
              </th>
            ) : null}
            <th scope="col">Severity</th>
            <th scope="col">Where</th>
            <th scope="col">Kind</th>
            <th scope="col">When</th>
            <th scope="col" className="esm-table--end">
              Occurrences
            </th>
            <th scope="col">What broke</th>
            <th scope="col">Affected</th>
            <th scope="col">
              <span className="esm-visually-hidden">Detail</span>
            </th>
          </tr>
        </thead>
        {rows.map((row) => (
          <FailureRow
            key={row.id}
            row={row}
            search={search}
            expanded={openRows.has(row.id)}
            onToggle={onToggleRow}
            onFilter={onFilter}
            mayClear={mayClear}
            selected={selected.has(row.id)}
            onSelect={onSelect}
          />
        ))}
      </table>
    </div>
  );
}

/**
 * One fault: a summary row, and the whole thing underneath it when it is open.
 *
 * The message is rendered as TEXT and nothing else. The recorder scrubs it and
 * its provider-side entry point takes no message parameter at all, so upstream
 * prose has no route to this column — but this console is where an operator
 * holding every permission in the realm is signed in, and the rule that nothing
 * from the log chooses its own markup is a rule with no exceptions.
 */
export function FailureRow({
  row,
  search,
  expanded,
  onToggle,
  onFilter,
  mayClear = false,
  selected = false,
  onSelect = () => {},
}: {
  row: Failure;
  search: FailuresSearch;
  expanded: boolean;
  onToggle: (id: string, open: boolean) => void;
  onFilter: (next: FailuresSearch) => void;
  mayClear?: boolean;
  selected?: boolean;
  onSelect?: (id: string, on: boolean) => void;
}) {
  const note = acknowledgementNote(row);
  return (
    <tbody className={row.acknowledged ? "esm-failure esm-failure--cleared" : "esm-failure"}>
      <tr>
        {mayClear ? (
          <td className="esm-failures__tick">
            {/* A cleared row has NO box. Acknowledging it again writes nothing
                — the original operator's decision is never overwritten — so a
                box there would be a control whose act is a reported no-op, and
                it would put rows into "the exact number that will be affected"
                that are not going to be affected at all. */}
            {row.acknowledged ? (
              <span className="esm-not-recorded" title="Already cleared">
                <span aria-hidden="true">—</span>
                <span className="esm-visually-hidden">
                  Already cleared, so there is nothing to clear
                </span>
              </span>
            ) : (
              <>
                <input
                  id={`failures-tick-${row.id}`}
                  type="checkbox"
                  checked={selected}
                  onChange={(event) => onSelect(row.id, event.target.checked)}
                />
                <label htmlFor={`failures-tick-${row.id}`} className="esm-visually-hidden">
                  {`Tick this ${row.severity} in ${row.service}: ${row.message}`}
                </label>
              </>
            )}
          </td>
        ) : null}
        <td>
          <Badge tone={severityTone(row.severity)}>{row.severity}</Badge>
          <div className="esm-muted-text">{note}</div>
        </td>
        <td>
          <code>{row.service}</code>
          {/* The component is a TEMPLATE — a package, a route pattern, a job
              kind — never a concrete path, because it is part of the
              fingerprint. It is the grouping key, so it reads as code. */}
          <div className="esm-muted-text">
            <code className="esm-long-value" title={row.component}>
              {row.component}
            </code>
          </div>
        </td>
        <td>
          <code>{row.kind}</code>
          {/* apperr's own classification, and null for a panic or a dead job —
              which is a fact about how the failure was recorded, not a gap. */}
          <div className="esm-muted-text">
            {row.error_code ? <code>{row.error_code}</code> : <span className="esm-not-recorded">no error code</span>}
          </div>
        </td>
        <td>
          {when(row.occurred_at)}
          {/* Both instants, because on a fingerprinted row they are different
              facts: the first is what the list is ordered and filtered by, and
              the second is whether the thing is still happening. */}
          <div className="esm-muted-text">last {when(row.last_occurred_at)}</div>
        </td>
        <td className="esm-table--end">
          {/* The number this table exists to make unmissable. A row standing
              for ten thousand failures must not read like one failure — so the
              figure is drawn large and its sentence is the accessible name. */}
          <span className="esm-occurrences" title={occurrenceLabel(row.occurrence_count)}>
            <span aria-hidden="true">{occurrenceCount(row.occurrence_count)}</span>
            <span className="esm-visually-hidden">{occurrenceLabel(row.occurrence_count)}</span>
          </span>
        </td>
        <td>
          <span className="esm-failure__message">{row.message}</span>
        </td>
        <td>
          <SubjectLinks row={row} />
        </td>
        <td>
          <button
            type="button"
            className="esm-disclose"
            aria-expanded={expanded}
            onClick={() => onToggle(row.id, !expanded)}
          >
            {expanded ? "Hide" : "Open"}
            <span className="esm-visually-hidden"> the whole fault</span>
          </button>
        </td>
      </tr>
      {expanded ? (
        <tr className="esm-failure__detail">
          {/* One more column when the tick boxes are drawn, or the panel stops
              short of the table's own width. */}
          <td colSpan={mayClear ? 9 : 8}>
            <FailureExpansion row={row} search={search} onFilter={onFilter} />
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}

/**
 * Who a fault happened to, as links to the pages those subjects already have.
 *
 * The ids are never withheld — they are what a console links on — and a null is
 * a FACT rather than a gap: a retention sweep that fails happens to nobody.
 *
 * An installation has no page of its own; it is a section on its owner's
 * account, so the device link is that account plus the anchor. A device with no
 * user on the row therefore has nowhere to go, and says so instead of rendering
 * a link that lands on nothing.
 */
function SubjectLinks({ row }: { row: Failure }) {
  if (!row.user_id && !row.device_id && !row.application_id) {
    return <span className="esm-not-recorded">nobody — this happened off a request</span>;
  }
  return (
    <ul className="esm-inline-list">
      {row.user_id ? (
        <li>
          <span className="esm-muted-text">account</span>{" "}
          <Link to="/users/$userId" params={{ userId: row.user_id }}>
            <ShortID value={row.user_id} />
          </Link>
        </li>
      ) : null}
      {row.device_id ? (
        <li>
          <span className="esm-muted-text">installation</span>{" "}
          {row.user_id ? (
            <Link
              to="/users/$userId"
              params={{ userId: row.user_id }}
              hash={`device-${row.device_id}`}
            >
              <ShortID value={row.device_id} />
            </Link>
          ) : (
            <>
              <ShortID value={row.device_id} />
              <span className="esm-not-recorded"> — no account on this fault, so no page</span>
            </>
          )}
        </li>
      ) : null}
      {row.application_id ? (
        <li>
          <span className="esm-muted-text">application</span>{" "}
          <Link to="/applications/$applicationId" params={{ applicationId: row.application_id }}>
            <ShortID value={row.application_id} />
          </Link>
        </li>
      ) : null}
    </ul>
  );
}

/**
 * The open row: one more read, for one fault.
 *
 * The list carries no `context` — 0039 bounds it at 8 KiB and a fifty-row page
 * carrying it would be four hundred kilobytes of detail read one row of — so
 * opening a row is what fetches it. The route writes no audit row, unlike the
 * app-metadata reads whose consent IS the disclosure, so this costs nothing but
 * the request.
 */
function FailureExpansion({
  row,
  search,
  onFilter,
}: {
  row: Failure;
  search: FailuresSearch;
  onFilter: (next: FailuresSearch) => void;
}) {
  const detail = useFailure(row.id);
  return (
    <FailureDetailPanel
      row={row}
      detail={detail.data}
      error={detail.error}
      search={search}
      onFilter={onFilter}
    />
  );
}

/**
 * Everything one fault says, and nothing the row remembers.
 *
 * Exported for the same reason `MetadataPanel` is: its three states are only
 * reachable by clicking, and a component whose branches need a browser is a
 * component whose branches are never proven.
 */
export function FailureDetailPanel({
  row,
  detail,
  error,
  search,
  onFilter,
}: {
  row: Failure;
  detail: FailureDetail | undefined;
  error: unknown;
  search: FailuresSearch;
  onFilter: (next: FailuresSearch) => void;
}) {
  const state = failureDetailState(Boolean(error), Boolean(detail));

  return (
    <div className="esm-failure__panel">
      {/* Off the ROW, so the three values an operator carries out of this
          console into a log search are available the instant it opens rather
          than after a round trip. */}
      <dl className="esm-detail-list">
        <dt>Fingerprint</dt>
        <dd>
          {/* The 32 bytes the table is keyed on. Two faults whose messages
              normalised to the same words are still tellable apart by this, and
              it is what to quote in a support conversation. */}
          <CopyText value={row.fingerprint} label="the fingerprint" />
        </dd>
        <dt>Request id</dt>
        <dd>
          {row.request_id ? (
            <CopyText value={row.request_id} label="the request id" />
          ) : (
            <span className="esm-not-recorded">not recorded — this failure was not on a request</span>
          )}
        </dd>
        <dt>Inference request id</dt>
        <dd>
          {/* A value to COPY, not a link to follow: it carries no foreign key,
              because the failure is very often the reason that request row does
              not exist. */}
          {row.inference_request_id ? (
            <CopyText value={row.inference_request_id} label="the inference request id" />
          ) : (
            <span className="esm-not-recorded">not recorded — no inference request</span>
          )}
        </dd>
        <dt>First seen</dt>
        <dd>{when(row.occurred_at)}</dd>
        <dt>Last fired</dt>
        <dd>
          {when(row.last_occurred_at)}{" "}
          <span className="esm-muted-text">({occurrenceLabel(row.occurrence_count)})</span>
        </dd>
      </dl>

      {row.acknowledged ? (
        <Alert
          tone={row.recurred ? "warning" : "info"}
          title={row.recurred ? "Cleared — and it has fired again since" : "Cleared"}
        >
          Acknowledged {when(row.acknowledged_at)}
          {row.acknowledged_by ? (
            <>
              {" "}
              by <ShortID value={row.acknowledged_by} />
            </>
          ) : null}
          {row.acknowledged_reason ? (
            <>
              {" — "}
              <code>{row.acknowledged_reason}</code>
            </>
          ) : null}
          {row.recurred ? (
            <>
              {" "}
              An acknowledgement is never undone by a repeat, deliberately: a fault firing several
              times a second would otherwise overturn an operator's decision over and over. This
              one is still happening.
            </>
          ) : null}
        </Alert>
      ) : null}

      <p className="esm-chips">
        <span className="esm-muted-text">Everything else like this:</span>
        <button
          type="button"
          className="esm-chip"
          onClick={() => onFilter({ ...search, service: row.service })}
        >
          in <code>{row.service}</code>
        </button>
        <button type="button" className="esm-chip" onClick={() => onFilter({ ...search, kind: row.kind })}>
          of kind <code>{row.kind}</code>
        </button>
        {row.user_id ? (
          <button
            type="button"
            className="esm-chip"
            onClick={() => onFilter({ ...search, user: row.user_id ?? undefined })}
          >
            on this account
          </button>
        ) : null}
        {row.device_id ? (
          <button
            type="button"
            className="esm-chip"
            onClick={() => onFilter({ ...search, device: row.device_id ?? undefined })}
          >
            on this installation
          </button>
        ) : null}
      </p>

      {state === "loading" ? (
        <Spinner inline label="Reading this fault" />
      ) : state === "failed" ? (
        <Alert tone="danger" title="Could not read the rest of this fault">
          {errorMessage(error)} The row above is what the list returned and is still true; the
          context and the resolved names are what is missing. A fault the retention sweep removed
          while this page was open answers 404 here.
        </Alert>
      ) : (
        <FailureContext detail={detail} />
      )}
    </div>
  );
}

/** The whole fault, once it has arrived: who it happened to by NAME, and the
 *  scrubbed structured detail. */
function FailureContext({ detail }: { detail: FailureDetail | undefined }) {
  if (!detail) {
    return null;
  }
  return (
    <>
      <dl className="esm-detail-list">
        {/* The application is NOT gated. It is a product rather than a person,
            and every failures:read holder reads it in full from
            /admin/v1/applications on the same session — withholding it here
            would protect nothing and make the console lie about what it knows. */}
        <dt>Application</dt>
        <dd>
          {detail.application ? (
            <Link
              to="/applications/$applicationId"
              params={{ applicationId: detail.application.id }}
            >
              {detail.application.name} <code>{detail.application.slug}</code>
            </Link>
          ) : (
            <span className="esm-not-recorded">no application on this fault</span>
          )}
        </dd>
        <dt>Account</dt>
        <dd>
          {/* THREE renders, not two. Absent means the operator may not see it
              (users:read); null means the fault happened to nobody, which is
              the common case for a worker sweep; present is the account. A
              permission boundary and a meaningful fact must never draw the
              same. */}
          {detail.user === undefined ? (
            <span className="esm-not-recorded">withheld — naming the account needs users:read</span>
          ) : detail.user === null ? (
            <span className="esm-not-recorded">this fault happened to nobody</span>
          ) : (
            <>
              <Link to="/users/$userId" params={{ userId: detail.user.id }}>
                {detail.user.display_name || detail.user.email}
              </Link>{" "}
              <span className="esm-muted-text">{detail.user.email}</span>{" "}
              <Badge tone={detail.user.status === "active" ? "success" : "danger"}>
                {detail.user.status}
              </Badge>
            </>
          )}
        </dd>
        <dt>Installation</dt>
        <dd>
          {detail.device === undefined ? (
            <span className="esm-not-recorded">withheld — naming the installation needs users:read</span>
          ) : detail.device === null ? (
            <span className="esm-not-recorded">no installation on this fault</span>
          ) : (
            <>
              {detail.user ? (
                <Link
                  to="/users/$userId"
                  params={{ userId: detail.user.id }}
                  hash={`device-${detail.device.id}`}
                >
                  {detail.device.device_name || detail.device.installation_id}
                </Link>
              ) : (
                <>{detail.device.device_name || detail.device.installation_id}</>
              )}{" "}
              <span className="esm-muted-text">{detail.device.platform}</span>
            </>
          )}
        </dd>
      </dl>

      <h4 className="esm-failure__context-title">Context</h4>
      {contextIsEmpty(detail.context) ? (
        <p className="esm-blank-value">
          This fault carried no structured detail. That is a recorder that had nothing to add, not
          a document that failed to arrive.
        </p>
      ) : (
        <p className="esm-muted-text">
          Identifiers and classifications only. The recorder scrubs it on the way in — a deny-list
          over keys at every depth, a closed set of value types, and a cap on size and shape — and
          nothing here is ever derived from a prompt or a completion.
        </p>
      )}
      {/* A text child, and the only thing this component does with the
          document. Whatever is in it comes out as characters. */}
      <pre className="esm-metadata__payload">
        <code>{contextText(detail.context)}</code>
      </pre>
    </>
  );
}
